/**
 * 🔗 BACKLINK AJANI — profesyonel düzeyde backlink denetimi:
 * profil KPI'ları + en güçlü kaynak domainler + anchor dağılımı + aylık yeni/kaybolan
 * trendi + BACKLINK FIRSATLARI (rakiplere link veren ama bize vermeyen siteler —
 * outreach hedefleri, öncelik puanıyla). AI yorumu ayrı motordan (kota yoksa denetim yine tam).
 */
import { confirmCost } from "@/components/cost-confirm";
import { useState, useMemo, useRef, useEffect } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Loader2, Link2, Search, Trophy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Area, AreaChart, Line, LineChart, Bar, BarChart, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ReferenceLine,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
} from "recharts";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c" };
const fmt = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));

type Opportunity = {
  domain: string; rank: number; spamScore: number; competitorCount: number;
  linksToCompetitors: { competitor: string; backlinks: number }[];
  priority: number; linkTypes?: string[];
};
type DetailedBacklink = {
  domainFrom: string; urlFrom: string; pageTitle: string;
  pageLang: string; country: string;
  pageAS: number; domainAS: number; spam: number;
  extLinks: number; intLinks: number;
  anchor: string | null; urlTo: string;
  itemType: string; semanticLocation: string;
  dofollow: boolean; attributes: string[];
  isNew: boolean; isLost: boolean;
  firstSeen: string | null; lastSeen: string | null; linksCount: number;
};
// Gap rakip keşfi pazarı (organik rakipler bu pazardan bulunur)
const MARKETS: { k: string; l: string }[] = [
  { k: "tr", l: "🇹🇷 Türkiye" }, { k: "en", l: "🇺🇸 ABD" }, { k: "en-gb", l: "🇬🇧 İngiltere" },
  { k: "de", l: "🇩🇪 Almanya" }, { k: "fr", l: "🇫🇷 Fransa" }, { k: "es", l: "🇪🇸 İspanya" },
  { k: "it", l: "🇮🇹 İtalya" }, { k: "ar", l: "🇸🇦 Arapça" }, { k: "ru", l: "🇷🇺 Rusya" }, { k: "nl", l: "🇳🇱 Hollanda" },
];
type AuditData = {
  target: string;
  summary: {
    rank: number; backlinks: number; referringDomains: number; referringMainDomains: number;
    nofollowDomains: number; brokenBacklinks: number; brokenPages: number; spamScore: number;
    firstSeen: string | null; tldTop: { tld: string; count: number }[];
    linkTypes: { type: string; count: number }[]; dofollowRatio: number;
  };
  topReferringDomains: { domain: string; rank: number; backlinks: number; spamScore: number; firstSeen: string | null; referringPages: number }[];
  anchors: { anchor: string | null; backlinks: number; referringDomains: number }[];
  // rank: DataForSEO'nun 0-1000 otorite puani. Backend gonderiyor ama tipte
  // yoktu; "as trendi" grafigi bu yuzden her zaman bos ciziliyordu.
  trend: { month: string; rank: number; backlinks: number; referringDomains: number; newBacklinks: number; lostBacklinks: number; newRefDomains: number; lostRefDomains: number }[];
  backlinkCompetitors: { target: string; rank: number; intersections: number }[];
  usedCompetitors: string[];
  opportunities: Opportunity[];
  market?: string | null;
  kpis?: { organicTraffic: number; organicKeywords: number };
  authorityBuckets?: { bucket: string; count: number }[];
  linkAttributes?: { attr: string; count: number }[];
  toxicity?: { overall: "Low" | "Medium" | "High"; toxicDomains: number; potentiallyToxic: number };
  topPages?: { url: string; referringDomains: number; backlinks: number; spamScore: number }[];
  detailedBacklinks?: DetailedBacklink[];
  detailedTotalCount?: number;
  aiComment?: string; aiCommentError?: string; cost: number;
};
type Resp = { configured: boolean; data: AuditData | null; runAt: string | null; running: boolean; runError: string | null };

const spamColor = (s: number) => (s >= 20 ? C.red : s >= 10 ? C.amber : C.green);
const rankColor = (r: number) => (r >= 300 ? C.green : r >= 100 ? C.amber : "#a3a3a0");

export default function BacklinkAudit() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [compInput, setCompInput] = useState("");
  const [oppSearch, setOppSearch] = useState("");
  const [minComp, setMinComp] = useState(1);
  const [market, setMarket] = useState("tr");
  // Detaylı tablo filtreleri (Semrush tarzı)
  const [blStatus, setBlStatus] = useState<"all" | "active" | "new" | "lost">("all");
  const [blFollow, setBlFollow] = useState<"all" | "follow" | "nofollow">("all");
  const [blLang, setBlLang] = useState<string>("all");
  const [blSearch, setBlSearch] = useState("");

  const { data: resp } = useQuery({
    queryKey: ["backlink-audit", activeDomainId],
    enabled: !!activeDomainId,
    refetchInterval: (q) => ((q.state.data as Resp | undefined)?.running ? 4000 : false),
    queryFn: async (): Promise<Resp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/backlink-audit`); return r.ok ? await r.json() : { configured: false, data: null, runAt: null, running: false, runError: null }; }
      catch { return { configured: false, data: null, runAt: null, running: false, runError: null }; }
    },
  });
  // DAYANIKLILIK ("T.filter is not a function" çökmesi): eski denetim kayıtlarında
  // dizi alanları eksik/farklı şekilde olabiliyor (jsonb) — hepsi diziye zorlanır,
  // sayfa tek bozuk kayıtla çökmez.
  const d = useMemo(() => {
    const ham = resp?.data ?? null;
    if (!ham) return null;
    const dizi = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
    return {
      ...ham,
      topReferringDomains: dizi(ham.topReferringDomains), anchors: dizi(ham.anchors),
      trend: dizi(ham.trend), backlinkCompetitors: dizi(ham.backlinkCompetitors),
      usedCompetitors: dizi(ham.usedCompetitors), opportunities: dizi(ham.opportunities),
      authorityBuckets: dizi(ham.authorityBuckets), linkAttributes: dizi(ham.linkAttributes),
      topPages: dizi(ham.topPages), detailedBacklinks: dizi(ham.detailedBacklinks),
    } as AuditData;
  }, [resp?.data]);
  const running = !!resp?.running;

  // Koşu bitince bildirim + tazele
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      if (resp?.runError) toast({ title: "❌ Backlink denetimi başarısız", description: resp.runError, variant: "destructive" });
      else toast({ title: "✅ Backlink denetimi tamamlandı", description: "Fırsat tablosu hazır." });
      queryClient.invalidateQueries({ queryKey: ["backlink-audit", activeDomainId] });
    }
    wasRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const run = useMutation({
    mutationFn: async () => {
      const competitors = compInput.split(/[,\n]/).map((s) => s.trim()).filter(Boolean).slice(0, 4);
      const r = await fetch(`/api/domains/${activeDomainId}/backlink-audit`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ market, ...(competitors.length ? { competitors } : {}) }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Başlatılamadı");
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "🔍 Denetim başlatıldı", description: "30-90 sn sürer — bitince bildirim gelir." });
      queryClient.invalidateQueries({ queryKey: ["backlink-audit", activeDomainId] });
    },
    onError: (e) => toast({ title: "Hata", description: (e as Error).message, variant: "destructive" }),
  });

  const opps = useMemo(() => {
    const q = oppSearch.toLowerCase();
    return (d?.opportunities ?? [])
      .filter((o) => o.competitorCount >= minComp)
      .filter((o) => !q || o.domain.toLowerCase().includes(q));
  }, [d, oppSearch, minComp]);


  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm" style={{ color: C.purple }}><Link2 className="w-4 h-4" /> Off-Page SEO</div>
          <h1 className="text-2xl font-bold text-foreground mt-0.5">Backlink Ajanı</h1>
          <p className="text-sm text-muted-foreground">Profil denetimi + rakiplere link veren ama sana vermeyen siteler (outreach hedefleri).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2.5 py-1 rounded-lg font-medium" style={running ? { background: `${C.cyan}18`, color: "#b12220" } : { background: "rgba(34,197,94,0.1)", color: "#039855" }}>
            {running ? "⏳ Denetleniyor" : "● Hazır"}
          </span>
        </div>
      </div>

      {/* Başlat paneli */}
      <Card className="mb-4"><CardContent className="p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold mb-1.5">🌍 Hedef pazar</div>
            <Select value={market} onValueChange={setMarket}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MARKETS.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[300px]">
            <div className="text-xs font-semibold mb-1.5">🎯 Gap rakipleri (opsiyonel — boş bırakırsan seçili pazardaki organik rakiplerin otomatik bulunur)</div>
            <input value={compInput} onChange={(e) => setCompInput(e.target.value)} placeholder="virgülle ayır: erdemhospital.com, memorial.com.tr, …"
              className="w-full text-sm px-3 py-2.5 rounded-xl border outline-none bg-white border-black/10 text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
          </div>
          <button disabled={!activeDomainId || running || run.isPending} onClick={async () => { if (await confirmCost("backlink")) run.mutate(); }}
            className="px-5 py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
            {running || run.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Denetleniyor…</> : <>🔍 {d ? "Yeniden Analiz" : "Denetimi Başlat"}</>}
          </button>
        </div>
        {d && resp?.runAt && <div className="text-[11px] text-muted-foreground mt-2">Son denetim: {new Date(resp.runAt).toLocaleString("tr-TR")} · kullanılan token {Math.round(d.cost * 1000).toLocaleString("tr-TR")} · gap rakipleri: {d.usedCompetitors.join(", ") || "—"}</div>}
        {resp?.runError && <div className="text-xs text-red-500 mt-2">❌ {resp.runError}</div>}
      </CardContent></Card>

      {!d && !running && (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          <Link2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Henüz denetim yok — "Denetimi Başlat"a bas. kapsamlı profil analizi + outreach fırsatları ~1 dakikada gelir.
        </CardContent></Card>
      )}

      {d && (<>
        {/* ── KPI kartları (Semrush üst şeridi) ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-4">
          {[
            { l: "Authority Score", v: String(Math.round(d.summary.rank / 10)), c: rankColor(d.summary.rank), s: `DFS rank ${d.summary.rank}/1000` },
            { l: "Backlink", v: fmt(d.summary.backlinks), c: undefined, s: `${fmt(d.summary.brokenBacklinks)} kırık` },
            { l: "Referring Domain", v: fmt(d.summary.referringMainDomains), c: undefined, s: `${d.summary.referringDomains} alt domain dahil` },
            { l: "Organik Trafik", v: d.kpis ? fmt(d.kpis.organicTraffic) : "—", c: undefined, s: d.kpis ? `${fmt(d.kpis.organicKeywords)} kelime` : "" },
            { l: "Dofollow Oranı", v: `%${Math.round(d.summary.dofollowRatio * 100)}`, c: d.summary.dofollowRatio >= 0.4 ? C.green : C.amber, s: `${d.summary.nofollowDomains} nofollow domain` },
            { l: "Toksisite", v: d.toxicity ? (d.toxicity.overall === "Low" ? "Düşük" : d.toxicity.overall === "Medium" ? "Orta" : "Yüksek") : String(d.summary.spamScore), c: d.toxicity ? (d.toxicity.overall === "Low" ? C.green : d.toxicity.overall === "Medium" ? C.amber : C.red) : spamColor(d.summary.spamScore), s: d.toxicity ? `${d.toxicity.toxicDomains} toksik · ${d.toxicity.potentiallyToxic} şüpheli domain` : "spam skoru" },
            { l: "Spam Skoru", v: String(d.summary.spamScore), c: spamColor(d.summary.spamScore), s: "hedefin kendisi" },
            { l: "Fırsat", v: String(d.opportunities.length), c: C.purple, s: "outreach hedefi" },
          ].map((k, i) => (
            <Card key={i}><CardContent className="p-3.5">
              <div className="text-[10px] text-muted-foreground">{k.l}</div>
              <div className="text-2xl font-bold tabular-nums" style={k.c ? { color: k.c } : undefined}>{k.v}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{k.s}</div>
            </CardContent></Card>
          ))}
        </div>

        {/* ── GRAFİK KATI (Semrush düzeni): AS radar + AS trend + RD alan / BL alan + yeni-kayıp barlar ── */}
        {(() => {
          const AS = Math.round(d.summary.rank / 10);
          // Radar eksenleri (0-100): Link Gücü, Organik Trafik (log ölçek), Doğal Profil (spam/nofollow cezalı)
          const trafficScore = d.kpis ? Math.min(100, Math.round(Math.log10(Math.max(1, d.kpis.organicTraffic)) * 20)) : 0;
          const naturalScore = Math.max(0, Math.round(100 - d.summary.spamScore * 2 - (d.toxicity ? d.toxicity.toxicDomains * 5 + d.toxicity.potentiallyToxic * 2 : 0)));
          const radarData = [
            { axis: "Link Gücü", v: AS },
            { axis: "Organik Trafik", v: trafficScore },
            { axis: "Doğal Profil", v: naturalScore },
          ];
          const asTrend = d.trend.filter((t) => (t.rank ?? 0) > 0).map((t) => ({ month: t.month.slice(2), as: Math.round((t.rank ?? 0) / 10) }));
          const areaData = d.trend.map((t) => ({ month: t.month.slice(2), rd: t.referringDomains, bl: t.backlinks }));
          const newLostRd = d.trend.map((t) => ({ month: t.month.slice(2), yeni: t.newRefDomains, kayip: -t.lostRefDomains }));
          const newLostBl = d.trend.map((t) => ({ month: t.month.slice(2), yeni: t.newBacklinks, kayip: -t.lostBacklinks }));
          const axisStyle = { fontSize: 10, fill: "#a3a3a0" };
          const tipStyle = { fontSize: 12, borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)" };
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              {/* Authority Score + radar */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Authority Score</div>
                <div className="flex items-center gap-2">
                  <span className="text-4xl font-bold tabular-nums" style={{ color: rankColor(d.summary.rank) }}>{AS}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${C.purple}15`, color: "#d42b29" }}>{AS >= 40 ? "İyi" : AS >= 20 ? "Ortalama" : "Zayıf"}</span>
                </div>
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} outerRadius="70%">
                      <PolarGrid stroke="rgba(0,0,0,0.08)" />
                      <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#70706c" }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="v" stroke={C.purple} fill={C.purple} fillOpacity={0.25} />
                      <RTooltip contentStyle={tipStyle} formatter={(v: number) => [`${v}/100`]} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>

              {/* AS Trend */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Authority Score Trendi <span className="font-normal">· son 12 ay</span></div>
                <div className="h-[210px]">
                  {asTrend.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={asTrend} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="month" tick={axisStyle} />
                        <YAxis domain={[0, 100]} tick={axisStyle} />
                        <RTooltip contentStyle={tipStyle} />
                        <Line type="monotone" dataKey="as" name="Authority Score" stroke={C.purple} strokeWidth={2.5} dot={{ r: 2.5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Trend için yeniden analiz gerekli (aylık AS yeni eklendi).</div>}
                </div>
              </CardContent></Card>

              {/* Referring Domains alan grafiği */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Referring Domains <span className="font-normal">· 1Y</span></div>
                <div className="h-[210px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={areaData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                      <defs><linearGradient id="rdG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.purple} stopOpacity={0.35} /><stop offset="100%" stopColor={C.purple} stopOpacity={0.02} /></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={axisStyle} />
                      <YAxis tick={axisStyle} width={48} tickFormatter={(v: number) => fmt(v)} />
                      <RTooltip contentStyle={tipStyle} formatter={(v: number) => [fmt(v), "Referring Domain"]} />
                      <Area type="monotone" dataKey="rd" stroke={C.purple} strokeWidth={2} fill="url(#rdG)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>

              {/* Backlinks alan grafiği */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Backlinks <span className="font-normal">· 1Y</span></div>
                <div className="h-[210px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={areaData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                      <defs><linearGradient id="blG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.cyan} stopOpacity={0.35} /><stop offset="100%" stopColor={C.cyan} stopOpacity={0.02} /></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={axisStyle} />
                      <YAxis tick={axisStyle} width={48} tickFormatter={(v: number) => fmt(v)} />
                      <RTooltip contentStyle={tipStyle} formatter={(v: number) => [fmt(v), "Backlink"]} />
                      <Area type="monotone" dataKey="bl" stroke={C.cyan} strokeWidth={2} fill="url(#blG)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>

              {/* Yeni / Kayıp Referring Domains */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Yeni & Kayıp Referring Domains</div>
                <div className="h-[210px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={newLostRd} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={axisStyle} />
                      <YAxis tick={axisStyle} />
                      <RTooltip contentStyle={tipStyle} formatter={(v: number, n: string) => [Math.abs(v), n === "yeni" ? "Yeni" : "Kayıp"]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: string) => (v === "yeni" ? "Yeni" : "Kayıp")} />
                      <ReferenceLine y={0} stroke="rgba(0,0,0,0.2)" />
                      <Bar dataKey="yeni" fill="#e83e3c" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="kayip" fill="#f87171" radius={[0, 0, 3, 3]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>

              {/* Yeni / Kayıp Backlinks */}
              <Card><CardContent className="p-4">
                <div className="text-[11px] font-semibold text-muted-foreground mb-1">Yeni & Kayıp Backlinks</div>
                <div className="h-[210px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={newLostBl} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={axisStyle} />
                      <YAxis tick={axisStyle} tickFormatter={(v: number) => fmt(Math.abs(v))} />
                      <RTooltip contentStyle={tipStyle} formatter={(v: number, n: string) => [fmt(Math.abs(v)), n === "yeni" ? "Yeni" : "Kayıp"]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: string) => (v === "yeni" ? "Yeni" : "Kayıp")} />
                      <ReferenceLine y={0} stroke="rgba(0,0,0,0.2)" />
                      <Bar dataKey="yeni" fill="#e83e3c" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="kayip" fill="#f87171" radius={[0, 0, 3, 3]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>
            </div>
          );
        })()}

        {/* ── AI yorumu ── */}
        {(d.aiComment || d.aiCommentError) && (
          <Card className="mb-4" style={{ borderColor: `${C.purple}44` }}><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-brand-600 dark:text-brand-400">🤖 Off-Page SEO Uzmanı Yorumu</div>
            {d.aiComment
              ? <div className="text-sm leading-relaxed whitespace-pre-wrap">{d.aiComment}</div>
              : <div className="text-xs text-warning-700 dark:text-warning-400">⚠️ {d.aiCommentError}</div>}
          </CardContent></Card>
        )}

        {/* ── FIRSAT TABLOSU — yıldız bölüm ── */}
        <Card className="mb-4" style={{ borderColor: `${C.purple}55` }}><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Trophy className="w-4 h-4" style={{ color: C.amber }} />
            <span className="font-semibold text-sm">Backlink Fırsatları</span>
            <span className="text-[11px] text-muted-foreground">— bu siteler {d.usedCompetitors.map((c) => c.split(".")[0]).join(" / ")} rakiplerine link veriyor ama SANA VERMİYOR</span>
            <div className="ml-auto flex items-center gap-2">
              <select value={minComp} onChange={(e) => setMinComp(Number(e.target.value))} className="text-xs border rounded-lg px-2 py-1.5 bg-white" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                <option value={1}>≥1 rakibe link</option>
                <option value={2}>≥2 rakibe link</option>
                <option value={3}>≥3 rakibe link</option>
              </select>
              <div className="flex items-center gap-1.5 border rounded-lg px-2 py-1.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                <Search className="w-3.5 h-3.5 text-muted-foreground" />
                <input value={oppSearch} onChange={(e) => setOppSearch(e.target.value)} placeholder="domain ara…" className="text-xs bg-transparent outline-none w-36" />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">Öncelik = kaç rakibe link verdiği + otorite − spam. Spam skoru 35 üstü kaynaklar otomatik elendi.</p>
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="sticky top-0" style={{ background: "#ffffff" }}><tr className="text-[11px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                <th className="py-1.5 pr-2">#</th><th className="py-1.5">Site (outreach hedefi)</th><th className="py-1.5 text-right">Öncelik</th><th className="py-1.5 text-right">Rank</th><th className="py-1.5 text-right">Spam</th><th className="py-1.5">Link verdiği rakipler</th><th className="py-1.5">Tür</th>
              </tr></thead>
              <tbody>{opps.map((o, i) => (
                <tr key={o.domain} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                  <td className="py-1.5 pr-2 text-muted-foreground tabular-nums">{i + 1}</td>
                  <td className="py-1.5 font-medium">
                    <a href={`https://${o.domain}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline decoration-dotted">
                      {o.domain} <ExternalLink className="w-3 h-3 opacity-40" />
                    </a>
                  </td>
                  <td className="py-1.5 text-right"><span className="text-[11px] px-1.5 py-0.5 rounded font-semibold tabular-nums" style={{ background: `${C.purple}15`, color: "#d42b29" }}>{o.priority}</span></td>
                  <td className="py-1.5 text-right tabular-nums" style={{ color: rankColor(o.rank) }}>{o.rank}</td>
                  <td className="py-1.5 text-right tabular-nums" style={{ color: spamColor(o.spamScore) }}>{o.spamScore}</td>
                  <td className="py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {o.linksToCompetitors.map((lc) => (
                        <span key={lc.competitor} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(6,182,212,0.08)", color: "#b12220" }}>
                          {lc.competitor.split(".")[0]} ×{fmt(lc.backlinks)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-1.5 text-[10px] text-muted-foreground">{(o.linkTypes ?? []).join(", ") || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
            {opps.length === 0 && <div className="text-xs text-muted-foreground p-4 text-center">Bu filtreyle fırsat yok — "≥1 rakibe link" seç veya aramayı temizle.</div>}
          </div>
        </CardContent></Card>

        {/* ── DETAYLI BACKLINK TABLOSU (Semrush "Backlink Audit" tarzı) ── */}
        {(d.detailedBacklinks?.length ?? 0) > 0 && (() => {
          const all = d.detailedBacklinks!;
          const langs = [...new Set(all.map((b) => b.pageLang || "?"))].sort();
          const q = blSearch.toLowerCase();
          const rows = all
            .filter((b) => blStatus === "all" || (blStatus === "new" ? b.isNew : blStatus === "lost" ? b.isLost : !b.isLost))
            .filter((b) => blFollow === "all" || (blFollow === "follow" ? b.dofollow : !b.dofollow))
            .filter((b) => blLang === "all" || (b.pageLang || "?") === blLang)
            .filter((b) => !q || b.pageTitle.toLowerCase().includes(q) || b.urlFrom.toLowerCase().includes(q) || (b.anchor ?? "").toLowerCase().includes(q));
          const exportCsv = () => {
            const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
            const head = ["Page AS", "Kaynak Domain", "Kaynak URL", "Başlık", "Dil", "Ülke", "Ext", "Int", "Anchor", "Hedef URL", "Tür", "Konum", "Follow", "Durum", "İlk Görülme", "Son Görülme"];
            const lines = rows.map((b) => [Math.round(b.pageAS / 10), b.domainFrom, b.urlFrom, b.pageTitle, b.pageLang, b.country, b.extLinks, b.intLinks, b.anchor ?? "", b.urlTo, b.itemType, b.semanticLocation, b.dofollow ? "follow" : "nofollow", b.isLost ? "kayıp" : b.isNew ? "yeni" : "aktif", (b.firstSeen ?? "").slice(0, 10), (b.lastSeen ?? "").slice(0, 10)].map((x) => esc(String(x))).join(","));
            const blob = new Blob(["﻿" + [head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `backlinks-${d.target}.csv`; a.click(); URL.revokeObjectURL(a.href);
          };
          const dt = (s: string | null) => (s ? s.slice(0, 10) : "—");
          return (
            <Card className="mb-4"><CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Link2 className="w-4 h-4" style={{ color: C.cyan }} />
                <span className="font-semibold text-sm">Backlink Detayları</span>
                <span className="text-[11px] text-muted-foreground">1-{all.length} gösteriliyor (toplam {fmt(d.detailedTotalCount ?? all.length)})</span>
                <Button size="sm" variant="outline" className="ml-auto text-xs gap-1" onClick={exportCsv}>⬇ CSV Export ({rows.length})</Button>
              </div>
              {/* Filtre şeridi — Semrush tarzı */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="flex rounded-lg border overflow-hidden text-xs" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                  {([["all", "Tümü"], ["active", "Aktif"], ["new", "Yeni"], ["lost", "Kayıp"]] as const).map(([k, l]) => (
                    <button key={k} onClick={() => setBlStatus(k)} className={`px-2.5 py-1.5 transition ${blStatus === k ? "" : "bg-white dark:bg-gray-800 dark:text-gray-300"}`} style={blStatus === k ? { background: `${C.cyan}18`, color: "#b12220", fontWeight: 600 } : undefined}>{l}</button>
                  ))}
                </div>
                <div className="flex rounded-lg border overflow-hidden text-xs" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                  {([["all", "Tümü"], ["follow", "Follow"], ["nofollow", "Nofollow"]] as const).map(([k, l]) => (
                    <button key={k} onClick={() => setBlFollow(k)} className={`px-2.5 py-1.5 transition ${blFollow === k ? "" : "bg-white dark:bg-gray-800 dark:text-gray-300"}`} style={blFollow === k ? { background: `${C.green}18`, color: "#039855", fontWeight: 600 } : undefined}>{l}</button>
                  ))}
                </div>
                {/* DİL AYRIMI — kaynak sayfanın dili (hedef dile göre link ayrımı) */}
                <div className="flex items-center gap-1 flex-wrap">
                  {["all", ...langs].map((l) => (
                    <button key={l} onClick={() => setBlLang(l)} className="text-[10px] px-2 py-1 rounded-full transition"
                      style={blLang === l ? { background: `${C.purple}22`, color: "#d42b29", fontWeight: 600 } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>
                      {l === "all" ? "Tüm diller" : l.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 border rounded-lg px-2 py-1.5 ml-auto" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                  <Search className="w-3.5 h-3.5 text-muted-foreground" />
                  <input value={blSearch} onChange={(e) => setBlSearch(e.target.value)} placeholder="başlık / URL / anchor ara…" className="text-xs bg-transparent outline-none w-44" />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
                <table className="w-full text-xs min-w-[1050px]">
                  <thead className="sticky top-0 z-10" style={{ background: "#ffffff" }}><tr className="text-[10px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                    <th className="py-1.5 pr-2">Page AS</th><th className="py-1.5">Kaynak sayfa</th><th className="py-1.5 text-right">Ext</th><th className="py-1.5 text-right">Int</th><th className="py-1.5">Anchor → Hedef</th><th className="py-1.5">İlk Görülme</th><th className="py-1.5">Son Görülme</th>
                  </tr></thead>
                  <tbody>{rows.map((b, i) => (
                    <tr key={i} className="border-b last:border-0 align-top" style={{ borderColor: "rgba(0,0,0,0.05)", opacity: b.isLost ? 0.65 : 1 }}>
                      <td className="py-2 pr-2">
                        <span className="inline-block text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: `${rankColor(b.pageAS)}18`, color: rankColor(b.pageAS) }}>{Math.round(b.pageAS / 10)}</span>
                      </td>
                      <td className="py-2 pr-3 max-w-[340px]">
                        <div className="font-medium truncate" title={b.pageTitle}>{b.pageTitle || b.domainFrom}</div>
                        <a href={b.urlFrom} target="_blank" rel="noopener noreferrer" className="text-[10px] underline decoration-dotted truncate block hover:text-cyan-600 text-blue-light-700 dark:text-blue-light-500">{b.urlFrom.replace(/^https?:\/\//, "").slice(0, 60)}</a>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {b.pageLang && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.purple}12`, color: "#d42b29" }}>{b.pageLang.toUpperCase()}</span>}
                          {b.country && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{b.country}</span>}
                          {b.spam >= 30 && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.red}15`, color: C.red }}>spam {b.spam}</span>}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{b.extLinks}</td>
                      <td className="py-2 text-right tabular-nums">{b.intLinks}</td>
                      <td className="py-2 pr-3 max-w-[320px]">
                        <div className="truncate" title={b.anchor ?? ""}>{b.anchor || <i className="text-muted-foreground">(görsel{b.itemType === "image" ? "" : " / boş"})</i>}</div>
                        <a href={b.urlTo} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground underline decoration-dotted truncate block">{b.urlTo.replace(/^https?:\/\//, "").slice(0, 55)}</a>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(6,182,212,0.1)", color: "#b12220" }}>{b.itemType === "image" ? "Görsel" : "Metin"}</span>
                          {b.semanticLocation && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{b.semanticLocation}</span>}
                          {!b.dofollow && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.amber}18`, color: "#b54708" }}>Nofollow</span>}
                          {b.linksCount > 20 && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>Sitewide ×{fmt(b.linksCount)}</span>}
                          {b.isNew && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.green}15`, color: "#039855" }}>Yeni</span>}
                          {b.isLost && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${C.red}15`, color: C.red }}>Kayıp: link kaldırıldı</span>}
                        </div>
                      </td>
                      <td className="py-2 tabular-nums text-muted-foreground whitespace-nowrap">{dt(b.firstSeen)}</td>
                      <td className="py-2 tabular-nums whitespace-nowrap" style={{ color: b.isLost ? C.red : undefined }}>{dt(b.lastSeen)}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {rows.length === 0 && <div className="text-xs text-muted-foreground p-4 text-center">Bu filtreyle satır yok.</div>}
              </div>
            </CardContent></Card>
          );
        })()}

        {/* ── Authority kovaları + Tür/Öznitelik + Top Pages (Semrush kartları) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          {(d.authorityBuckets?.length ?? 0) > 0 && (
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-semibold mb-2 text-blue-light-700 dark:text-blue-light-500">📊 Referring Domain — Authority Score Dağılımı</div>
              {(() => {
                const total = d.authorityBuckets!.reduce((s, b) => s + b.count, 0) || 1;
                return [...d.authorityBuckets!].reverse().map((b) => {
                  const pct = (b.count / total) * 100;
                  return (
                    <div key={b.bucket} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="w-14 text-muted-foreground tabular-nums shrink-0">{b.bucket}</span>
                      <div className="flex-1 h-2.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)" }}>
                        <div className="h-2.5 rounded-full" style={{ width: `${Math.max(b.count ? 2 : 0, pct)}%`, background: C.purple, opacity: 0.75 }} />
                      </div>
                      <span className="w-16 text-right tabular-nums text-muted-foreground shrink-0">%{Math.round(pct)} · {b.count}</span>
                    </div>
                  );
                });
              })()}
            </CardContent></Card>
          )}

          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-brand-600 dark:text-brand-400">🔗 Backlink Türleri</div>
            {(() => {
              const total = d.summary.linkTypes.reduce((s, t) => s + t.count, 0) || 1;
              const label: Record<string, string> = { anchor: "Metin", image: "Görsel", redirect: "Yönlendirme", canonical: "Canonical", alternate: "Alternate", form: "Form", frame: "Frame" };
              return d.summary.linkTypes.map((t) => (
                <div key={t.type} className="flex items-center gap-2 text-xs py-1">
                  <span className="w-20 shrink-0">{label[t.type] ?? t.type}</span>
                  <div className="flex-1 h-2.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)" }}>
                    <div className="h-2.5 rounded-full" style={{ width: `${Math.max(1, (t.count / total) * 100)}%`, background: C.cyan }} />
                  </div>
                  <span className="w-20 text-right tabular-nums text-muted-foreground shrink-0">%{Math.round((t.count / total) * 100)} · {fmt(t.count)}</span>
                </div>
              ));
            })()}
            {(d.linkAttributes?.length ?? 0) > 0 && (<>
              <div className="text-[11px] font-semibold mb-2 mt-4 text-success-600 dark:text-success-500">🏷 Link Öznitelikleri</div>
              {(() => {
                const total = d.linkAttributes!.reduce((s, a) => s + a.count, 0) || 1;
                return d.linkAttributes!.map((a) => (
                  <div key={a.attr} className="flex items-center gap-2 text-xs py-1">
                    <span className="w-20 shrink-0">{a.attr}</span>
                    <div className="flex-1 h-2.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)" }}>
                      <div className="h-2.5 rounded-full" style={{ width: `${Math.max(a.count ? 1 : 0, (a.count / total) * 100)}%`, background: a.attr === "Follow" ? C.green : C.amber }} />
                    </div>
                    <span className="w-20 text-right tabular-nums text-muted-foreground shrink-0">%{Math.round((a.count / total) * 100)} · {fmt(a.count)}</span>
                  </div>
                ));
              })()}
            </>)}
          </CardContent></Card>

          {(d.topPages?.length ?? 0) > 0 && (
            <Card><CardContent className="p-4">
              <div className="text-[11px] font-semibold mb-2 text-warning-700 dark:text-warning-400">🏆 En Çok Link Alan Sayfalar</div>
              <table className="w-full text-xs">
                <thead><tr className="text-[10px] text-muted-foreground text-left border-b"><th className="py-1">URL</th><th className="py-1 text-right">Ref. Domain</th><th className="py-1 text-right">Backlink</th></tr></thead>
                <tbody>{d.topPages!.map((p) => (
                  <tr key={p.url} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
                    <td className="py-1.5 pr-2 max-w-[240px]">
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted truncate block hover:text-cyan-600" title={p.url}>
                        {p.url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 45) || "/"}
                      </a>
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">{fmt(p.referringDomains)}</td>
                    <td className="py-1.5 text-right tabular-nums text-muted-foreground">{fmt(p.backlinks)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </CardContent></Card>
          )}
        </div>

        {/* ── Alt bölümler: kaynaklar + anchor + trend ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-blue-light-700 dark:text-blue-light-500">🏛 En Güçlü Kaynak Domainler</div>
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-[10px] text-muted-foreground text-left border-b"><th className="py-1">Domain</th><th className="py-1 text-right">Rank</th><th className="py-1 text-right">Link</th><th className="py-1 text-right">Spam</th></tr></thead>
                <tbody>{d.topReferringDomains.slice(0, 30).map((r) => (
                  <tr key={r.domain} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
                    <td className="py-1 pr-2 truncate max-w-[180px]" title={r.domain}>{r.domain}</td>
                    <td className="py-1 text-right tabular-nums" style={{ color: rankColor(r.rank) }}>{r.rank}</td>
                    <td className="py-1 text-right tabular-nums">{fmt(r.backlinks)}</td>
                    <td className="py-1 text-right tabular-nums" style={{ color: spamColor(r.spamScore) }}>{r.spamScore}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-brand-600 dark:text-brand-400">⚓ Anchor Dağılımı</div>
            <div className="max-h-[380px] overflow-y-auto space-y-1.5">
              {d.anchors.slice(0, 25).map((a, i) => {
                const total = d.anchors.reduce((s, x) => s + x.backlinks, 0) || 1;
                const pct = (a.backlinks / total) * 100;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate max-w-[220px]" title={a.anchor ?? ""}>{a.anchor || <i className="text-muted-foreground">(görsel / boş anchor)</i>}</span>
                      <span className="tabular-nums text-muted-foreground shrink-0">{fmt(a.backlinks)} · %{pct.toFixed(1)}</span>
                    </div>
                    <div className="h-1 rounded-full mt-0.5" style={{ background: "rgba(0,0,0,0.05)" }}>
                      <div className="h-1 rounded-full" style={{ width: `${Math.max(2, pct)}%`, background: pct > 30 ? C.amber : C.cyan }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-2 text-success-600 dark:text-success-500">🧬 Benzer Backlink Profilli Domainler</div>
            <p className="text-[10px] text-muted-foreground mb-2">Aynı kaynaklardan link alan domainler — link profili anlamında en yakın rakipler.</p>
            {d.backlinkCompetitors.map((c) => (
              <div key={c.target} className="flex items-center justify-between text-xs py-1 border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
                <span className="truncate max-w-[220px]">{c.target}</span>
                <span className="text-muted-foreground tabular-nums shrink-0">AS {Math.round(c.rank / 10)} · {c.intersections} ortak kaynak</span>
              </div>
            ))}
            {d.backlinkCompetitors.length === 0 && <div className="text-xs text-muted-foreground">Rakip karşılaştırması boş — bu koşuda ortak kelime rakibi bulunamadı; bir sonraki denetim koşusunda yeniden denenir.</div>}
          </CardContent></Card>
        </div>
      </>)}

      {/* FAZ 4.3 — BACKLINK FIRSATLARI: linkbait + kırık rakip sayfaları (DFS ücretli, elle tetik) */}
      <p className="mt-4 text-[11.5px] text-muted-foreground">🎣 Rakip avları (link mıknatısı içerikler + kırık ama backlink'li sayfalar) artık <a href="/backlink-firsat" className="font-medium text-brand-600 underline">Backlink Fırsat Ajanı</a> ekranının "Rakip Avları" sekmesinde.</p>
    </div>
  );
}

